/**
 * Per-world content split (docs/PERF.md "Per-world content split"). A full content pack is cut into
 *  - a small SHELL: every id, title, level and the whole structure (paths, mini units without their items, playground,
 *    challenges, …) with LIGHT drawings (no `steps` strokes / `art` / remix swaps, `$n` = step count, `$sig` = thumbnail
 *    signature), plus the lookups the app needs before any world is loaded (owning chunk of every drawing, chunks a
 *    lesson needs, world plans, lesson levels, story owners);
 *  - one CHUNK per world (+ `shared` for studio parts): the heavy drawing fields, the mini-unit items and the world's
 *    story layer.
 * The client keeps the shell in memory and hydrates drawings / mini units IN PLACE when a chunk arrives, so every map
 * (IX) keeps pointing at the same objects. Pure data: used by the client build script, the server release builder
 * and tests (round trip = the original pack).
 */
import type { ContentPack, Drawing, MiniUnit, MiniUnitItem, PathId, RemixInfo } from './model';
import type { PathPlan } from './worlds/types';
import type { PathPlanV2 } from './v2/types';
import type { WorldStoryLayer } from '../story/types';
import type { ArtFigure } from './art/types';

export const SHARED_CHUNK = 'shared';
export type ChunkId = PathId | typeof SHARED_CHUNK;
export const SHELL_SCHEMA = 1;

/** Heavy part of a drawing (lives in its world chunk). */
export interface DrawingHeavy {
  steps: Drawing['steps'];
  art?: ArtFigure;
  remix?: RemixInfo;
}

/** A drawing as the shell carries it: `steps` empty, no `art`, remix without swaps until its chunk is loaded. */
export type LightDrawing = Drawing & { $light?: true; $n?: number; $sig?: string };

export interface WorldChunk {
  schema: number;
  chunk: ChunkId;
  /** Content hash of this chunk (sha-like, computed by the builder). */
  hash: string;
  drawings: Record<string, DrawingHeavy>;
  /** Mini-unit items of the lessons this chunk owns. */
  miniUnits: Record<string, MiniUnitItem[]>;
  story?: WorldStoryLayer;
}

export interface ContentShell {
  schema: number;
  curriculum: 1 | 2;
  version: number;
  /** Light pack (drawings light, mini units with `items: []` and `$n` = item count). */
  pack: ContentPack;
  /** chunk id → content hash (delta updates: only chunks whose hash changed are downloaded). */
  chunks: Partial<Record<ChunkId, string>>;
  /** Owning chunk of every drawing. */
  drawingChunk: Record<string, ChunkId>;
  /** Chunks a mini unit needs besides nothing else (its drawings, the drawings they are built from, story worlds). */
  deps: Record<string, ChunkId[]>;
  /** Extra chunks a drawing needs beyond its own (remix base / scene figures in other worlds). Sparse. */
  drawingDeps: Record<string, ChunkId[]>;
  worlds: { id: PathId; plan: PathPlan; planV2?: PathPlanV2 }[];
  doodleOwners: Record<string, PathId>;
  /** Core `DRAWING_WORLD` (v1 world drawings + doodle owners): card backgrounds / themes outside the map. */
  drawingWorldV1: Record<string, PathId>;
  lessonLevel: Record<string, number>;
  /** Drawing id → world whose story narrates / features it. */
  storyWorld: Record<string, PathId>;
  /** World → story title + cast summary needed before the world's chunk loads (Collect counts, Residents shelf). */
  storyCast: Record<string, { title: WorldStoryLayer['title']; host: string; cast: { id: string; name: WorldStoryLayer['cast'][number]['name']; drawingId?: string }[] }>;
}

export const lightSig = (art: { vb: readonly number[]; layers: readonly unknown[] }) => `${art.layers.length}:${art.vb.join('x')}`;
export const isLightDrawing = (d: Drawing | undefined): boolean => !!d && (d as LightDrawing).$light === true;
/** Number of steps, also for a light drawing. */
export const stepCount = (d: Drawing | undefined): number => (d ? ((d as LightDrawing).$n ?? d.steps.length) : 0);
/** True when the drawing has (or will have, once its chunk loads) a figure. */
export const hasArt = (d: Drawing | undefined): boolean => !!d && (!!d.art || !!(d as LightDrawing).$sig);
/** Mini-unit item count, also for a light mini unit. */
export const itemCount = (mu: MiniUnit | undefined): number => (mu ? ((mu as MiniUnit & { $n?: number }).$n ?? mu.items.length) : 0);

function walkStrings(x: unknown, out: Set<string>): Set<string> {
  if (typeof x === 'string') out.add(x);
  else if (Array.isArray(x)) for (const v of x) walkStrings(v, out);
  else if (x && typeof x === 'object') for (const v of Object.values(x)) walkStrings(v, out);
  return out;
}

/** Tiny stable hash (FNV-1a 53-bit, hex) of a JSON value. */
export function contentHash(value: unknown): string {
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  let h1 = 0x811c9dc5 ^ 0, h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619);
    h2 = Math.imul(h2 ^ c, 2246822519);
  }
  return ((h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0')).slice(0, 14);
}

export interface SplitInput {
  pack: ContentPack;
  /** Owning world of drawings (v2 first, then v1 / doodle owners); missing = shared. */
  drawingWorld: Record<string, PathId>;
  worlds: { id: PathId; plan: PathPlan; planV2?: PathPlanV2 }[];
  doodleOwners: Record<string, PathId>;
  drawingWorldV1: Record<string, PathId>;
  lessonLevel: Record<string, number>;
  stories: WorldStoryLayer[];
  /** Story owner of a drawing (core `storyWorldOf`). */
  storyWorldOf: (drawingId: string) => string | undefined;
}

/** Cut a full pack into the shell + chunks (the pack is not modified). */
export function splitContent(input: SplitInput): { shell: ContentShell; chunks: WorldChunk[] } {
  const { pack } = input;
  const ids = new Set(pack.drawings.map((d) => d.id));
  const chunkOf = (id: string): ChunkId => input.drawingWorld[id] ?? SHARED_CHUNK;
  const chunks = new Map<ChunkId, WorldChunk>();
  const chunk = (c: ChunkId) => {
    let w = chunks.get(c);
    if (!w) chunks.set(c, (w = { schema: SHELL_SCHEMA, chunk: c, hash: '', drawings: {}, miniUnits: {} }));
    return w;
  };
  const drawingChunk: Record<string, ChunkId> = {};
  const lightDrawings: Drawing[] = [];
  const refsOf = (x: unknown) => [...walkStrings(x, new Set())].filter((s) => ids.has(s));
  const byId = new Map(pack.drawings.map((d) => [d.id, d] as const));
  const storyWorld: Record<string, PathId> = {};
  // Chunks a drawing needs: its own + (transitively) the drawings its light record points at (remix base, scene figures…).
  const drawingNeeds = (id: string, seen = new Set<string>()): Set<ChunkId> => {
    const out = new Set<ChunkId>();
    if (seen.has(id)) return out;
    seen.add(id);
    out.add(chunkOf(id));
    const d = byId.get(id);
    if (!d) return out;
    const sw = input.storyWorldOf(id);
    if (sw) out.add(sw as PathId);
    const { steps: _s, art: _a, ...rest } = d;
    for (const r of refsOf(rest)) if (r !== id) for (const c of drawingNeeds(r, seen)) out.add(c);
    return out;
  };
  const drawingDeps: Record<string, ChunkId[]> = {};
  for (const d of pack.drawings) {
    const c = chunkOf(d.id);
    drawingChunk[d.id] = c;
    const { steps, art, remix, ...rest } = d;
    chunk(c).drawings[d.id] = { steps, ...(art ? { art } : {}), ...(remix ? { remix } : {}) };
    const light: LightDrawing = { ...rest, steps: [], $light: true, $n: steps.length, ...(art ? { $sig: lightSig(art) } : {}) };
    if (remix) light.remix = { ...remix, swaps: [] };
    lightDrawings.push(light);
    const sw = input.storyWorldOf(d.id);
    if (sw) storyWorld[d.id] = sw as PathId;
    const needs = [...drawingNeeds(d.id)].filter((x) => x !== c);
    if (needs.length) drawingDeps[d.id] = needs.sort();
  }
  const deps: Record<string, ChunkId[]> = {};
  const lightMus: MiniUnit[] = pack.miniUnits.map((mu) => {
    const refs = refsOf(mu.items);
    const own = mu.drawingId && ids.has(mu.drawingId) ? chunkOf(mu.drawingId) : refs.length ? chunkOf(refs[0]!) : SHARED_CHUNK;
    chunk(own).miniUnits[mu.id] = mu.items;
    const need = new Set<ChunkId>([own]);
    for (const r of [...(mu.drawingId ? [mu.drawingId] : []), ...refs]) for (const c of drawingNeeds(r)) need.add(c);
    deps[mu.id] = [...need].sort();
    return { ...mu, items: [], $n: mu.items.length } as MiniUnit;
  });
  for (const s of input.stories) chunk(s.world as PathId).story = s;
  const out = [...chunks.values()];
  for (const c of out) c.hash = contentHash({ d: c.drawings, m: c.miniUnits, s: c.story ?? null });
  const storyCast: ContentShell['storyCast'] = Object.fromEntries(
    input.stories.map((s) => [s.world, { title: s.title, host: s.host, cast: s.cast.map((c) => ({ id: c.id, name: c.name, ...(c.drawingId ? { drawingId: c.drawingId } : {}) })) }]),
  );
  const shell: ContentShell = {
    schema: SHELL_SCHEMA,
    curriculum: pack.curriculum === 2 ? 2 : 1,
    version: pack.version,
    pack: { ...pack, drawings: lightDrawings, miniUnits: lightMus },
    chunks: Object.fromEntries(out.map((c) => [c.chunk, c.hash])),
    drawingChunk,
    deps,
    drawingDeps,
    worlds: input.worlds,
    doodleOwners: input.doodleOwners,
    drawingWorldV1: input.drawingWorldV1,
    lessonLevel: input.lessonLevel,
    storyWorld,
    storyCast,
  };
  return { shell, chunks: out };
}

/**
 * Put a chunk's heavy data into the live (light) objects, in place. Returns how many drawings / mini units were filled.
 * Drawings or mini units the shell does not know are ignored (a chunk never adds structure — the shell does).
 */
export function hydrateChunk(drawings: Map<string, Drawing>, miniUnits: Map<string, MiniUnit>, chunk: WorldChunk): { drawings: number; miniUnits: number } {
  let nd = 0;
  let nm = 0;
  for (const [id, h] of Object.entries(chunk.drawings)) {
    const d = drawings.get(id) as LightDrawing | undefined;
    if (!d) continue;
    d.steps = h.steps;
    if (h.art) d.art = h.art;
    if (h.remix) d.remix = h.remix;
    delete d.$light;
    nd++;
  }
  for (const [id, items] of Object.entries(chunk.miniUnits)) {
    const mu = miniUnits.get(id);
    if (!mu) continue;
    mu.items = items;
    nm++;
  }
  return { drawings: nd, miniUnits: nm };
}

/** Every string of a chunk / shell (catalog subsets: keep only the translations this chunk can ask for). */
export function chunkStrings(x: unknown): Set<string> {
  return walkStrings(x, new Set());
}

/** Catalog entries (English key → translation) whose key appears in `strings`. */
export function catalogSubset(catalog: Record<string, string>, strings: Set<string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(catalog)) if (strings.has(k)) out[k] = v;
  return out;
}
